import { useContext, useEffect, useRef } from 'react';
import { Formik, FormikErrors, FormikProps } from 'formik';
import { Box, Button, Typography } from '@mui/material';
import { IIneligibleSettingAPI, IFormikValuesForRuleAndOverridesPerSetting, IIneligibleCondition, IIneligibleRuleConditionGroup, IIneligibleRuleOverride, IIneligibleSettingsContext, IIneligibleSettingDetail, IneligibleRuleAPI, IIneligibleSettingsPermissions } from '../../../../interfaces/ineligibleSettingInterface';
import { IFormikValue as IFormikValueForAdjustments } from '../../../../interfaces/ineligibleAdjustmentsInterface';
import { NON_EXISTING, NON_EXISTING_PERCENT, NOT_APPLICABLE, PERMISSIONS } from '../../../../utility/constants';
import { getPermissionsOfUser, isObjectsEqual, timeout } from '../../../../utility/helper';
import { AuthContext } from '../../../../context/authContext';
import { SelectedClientContext } from '../../../../context/selectedClientContext';
import { IneligibleSettingsContext } from '../../../../context/ineligibleSettingsContext';
import { validateIneligibleSettings } from '../../../../schemas/ineligibleSettingsSchema';
import Toaster from '../../../toaster';
import ARCollateralHeader from '../../collateral-header/ar-collateral-header';
import { InvalidIneligibleSettingsMessageModal } from '../../modals';
import IneligibleList from '../../ineligible-settings-list';
import IneligibleDetails, { getIneligibleRuleOverrideDetails } from '../../ineligible-setting-details';
import styles from '../styles';
import NoDataPlaceholder from '../../../common/no-data-placeholder';
import DisabledComponentsContainer from '../../../common/disabled-components-container';
import { IARCollateral, IClient, ICollateral } from '../../../../interfaces';
import { getIneligibleRule, getIneligibleRuleOverrides, getIneligibleSettings, getInvIneligibleRule, getInvIneligibleRuleOverrides, getInvIneligibleSettings, postIneligibleRule, postInvIneligibleRule, putIneligibleOverrides, putIneligibleRule, putIneligibleSetting, putInvIneligibleOverrides, putInvIneligibleRule, putInvIneligibleSetting } from '../../../../api/ineligible-settings';
import { handleCreateOverrideAndConnections, handleDeleteCondition, handleDeleteConditionGroup, handleDeleteOverride, handleUpdatesForIneligibleRuleConditionGroups } from '..';
import { useNavigate } from 'react-router-dom';

/**
 * This function generates a default ineligible rule object based on an ineligible setting.
 * @param ineligibleSetting The ineligible setting.
 * @returns A default ineligible rule object.
 */
export const getDefaultIneligibleRule = (selectedCollateral: ICollateral, ineligibleSetting: IIneligibleSettingAPI, hasPaymentTerms: boolean) => {
  return {
    ineligibleRuleId: NON_EXISTING,
    ineligibleSettingId: ineligibleSetting.ineligibleSettingId,
    arCollateralId: selectedCollateral.recordId,
    invCollateralId: selectedCollateral.recordId,
    includeCredit: true,
    capAr: ineligibleSetting?.code === 'CN',
    arGtZero: false,
    childCustomerOnly: false,
    dpid: NON_EXISTING,
    dpdd: hasPaymentTerms ? NON_EXISTING : NOT_APPLICABLE,
    crossAgePct: NON_EXISTING_PERCENT,
    concentrationPct: NON_EXISTING_PERCENT,
    creditLimit: NON_EXISTING,
  };
};

/**
 * This asynchronous function validates a Formik form and returns errors related to ineligible settings.
 * It checks for errors in ineligible rules and their overrides, including condition groups and conditions.
 * @param formik FormikProps for the form.
 * @returns True if any errors are found, false otherwise.
 */
export const getErrors = async (formik: FormikProps<IFormikValuesForRuleAndOverridesPerSetting>) => {
  const errors = await formik.validateForm();
  const settingErrors = errors.ineligibleSettingDetails as FormikErrors<IIneligibleSettingDetail>[] ?? [];
  
  const hasError = settingErrors.some(setting => {
    const hasIneligibleRuleErrors = setting?.ineligibleRule !== undefined && Object.keys(setting?.ineligibleRule).length > 0;
    if (hasIneligibleRuleErrors) { return true; }
    let hasIneligibleRuleOverrideErrors = false;
    if (setting?.ineligibleRuleOverrides !== undefined && setting?.ineligibleRuleOverrides.length > 0) {
      const overrideErrors = setting?.ineligibleRuleOverrides as unknown as FormikErrors<IIneligibleRuleOverride>[] ?? [];
      hasIneligibleRuleOverrideErrors = overrideErrors.some(overrideError => {
        const filteredOverrideKeys = Object.keys(overrideError ?? {}).filter(key => key !== 'ineligibleRuleConditionGroup');
        if (filteredOverrideKeys.length > 0) { return true; }
        const conditionGroupErrors = overrideError?.ineligibleRuleConditionGroup as FormikErrors<IIneligibleRuleConditionGroup>[] ?? [];
        return conditionGroupErrors.some(conditionGroupError => {
          const conditionErrors = conditionGroupError?.ineligibleRuleCondition as FormikErrors<IIneligibleCondition>[] ?? [];
          return conditionErrors.some(conditionError => Object.keys(conditionError ?? {}).length > 0);
        });
      });
    }
    return hasIneligibleRuleOverrideErrors;
  });
  return hasError;
};

/**
 * Component represents the IneligibleList main form container.
 * @returns The IneligibleSettingsFormContainer component.
 */
const IneligibleSettingsARFormContainer = () => {
  const { selectedClient }                                      = useContext(SelectedClientContext);
  const { state: authState }                                    = useContext(AuthContext);
  const {
    selectedCollateral,
    selectedIneligibleIndex, setSelectedIneligibleIndex,
    ineligibleSettingDetails, setIneligibleSettingDetails,
    fetchedIneligibleSettingDetails, setFetchedIneligibleSettingDetails,
    setAsOfDates, setMostRecentlyUpdatedAsOfDate,
    setSelectedAdjustmentsAsOfDate, setIsAdjustmentsDirty,
    hasPaymentTerms, setHasPaymentTerms,
    setCurrency, setFieldValues,
    fieldList, setFieldList,
    fieldTables, setFieldTables,
    userDefinedFieldList, setUserDefinedFieldList,
    parentCustomers, setParentCustomers,
    toaster, setToaster,
    isFormikDirty, setIsFormikDirty,
    isAdjustmentsDirty,
    isInvalidIneligibleSettingsMessageModalOpen,
    setIsInvalidIneligibleSettingsMessageModalOpen,
    isIneligibleDetailsLoading, setIsIneligibleDetailsLoading,
    permissions, setPermissions
  }                                                             = useContext(IneligibleSettingsContext) as IIneligibleSettingsContext;
  const navigate = useNavigate();
  const adjustmentsFormikRef = useRef<FormikProps<IFormikValueForAdjustments> | null>(null);

  /**
   * This effect gets all ineligible settings related data when selected collateral is changed.
   */
  useEffect(() => {
    let isMounted = true;
    (
      async () => {
        if (selectedCollateral?.recordId === undefined) { return; }
        const nonNullSelectedClient = selectedClient as Required<IClient>;
        try {
          const values = await getAllValuesForIneligibleSettings(nonNullSelectedClient, selectedCollateral);
          if (!isMounted) { return; }
          setAllValuesForIneligibleSettings(values.ineligibleSettings, values.selectedIneligibleIndex, values.fetchedIneligibleSettingDetails);
        } catch (error) {
          console.log('FETCHING INELIGIBLE SETTINGS ERROR: ', error);
          if (isMounted) { setIsIneligibleDetailsLoading(false); }
        }
      }
    )();
    return () => { isMounted = false; };
  }, [selectedCollateral?.recordId]);

  /**
   * This effect gets permissions when auth state is changed.
   */
  useEffect(() => {
    getPermissions();
  }, [authState]);

  const reloadIneligibleSettings = async () => {
    if (selectedCollateral?.recordId === undefined) { return; }

    const nonNullSelectedClient = selectedClient as Required<IClient>;
    const values = await getAllValuesForIneligibleSettings(
      nonNullSelectedClient,
      selectedCollateral
    );
    setAllValuesForIneligibleSettings(
      values.ineligibleSettings,
      values.selectedIneligibleIndex,
      values.fetchedIneligibleSettingDetails
    );
  };

  /**
   * This function retrieves user permissions and updates state variables based on the user's permissions.
   */
  const getPermissions = async () => {
    if (permissions !== 'UNLOADED') { return; }
    const fetchedPermissions = await getPermissionsOfUser(authState.uid);

    if (!fetchedPermissions.includes(PERMISSIONS.VIEW_INELIGIBLE_SETTINGS)) {
      navigate('/forbidden');
      return;
    }

    setPermissions({
      canViewIneligibleSettings: fetchedPermissions.includes(PERMISSIONS.VIEW_INELIGIBLE_SETTINGS),
      canReorderList: fetchedPermissions.includes(PERMISSIONS.REORDER_INELIGIBLE_LIST),
      canActivateDeactivateIneligible: fetchedPermissions.includes(PERMISSIONS.ACTIVATE_DEACTIVATE_INELIGIBLE),
      canUpdateAdditionalSettings: fetchedPermissions.includes(PERMISSIONS.UPDATE_ADDITIONAL_SETTINGS),
      canCalculateIneligibles: fetchedPermissions.includes(PERMISSIONS.INELIGIBLE_SETTINGS_CALCULATE_INELIGIBLE),
      canAddCriteriaAndSettings: fetchedPermissions.includes(PERMISSIONS.ADD_CRITERIA_AND_SETTINGS),
      canUpdateCriteriaAndSettings: fetchedPermissions.includes(PERMISSIONS.UPDATE_CRITERIA_AND_SETTINGS),
      canDeleteCriteriaAndSettings: fetchedPermissions.includes(PERMISSIONS.DELETE_CRITERIA_AND_SETTINGS),
      canAddAdjustments: fetchedPermissions.includes(PERMISSIONS.ADD_IA),
      canUpdateAdjustments: fetchedPermissions.includes(PERMISSIONS.UPDATE_IA),
      canDeleteArchiveAdjustments: fetchedPermissions.includes(PERMISSIONS.DELETE_ARCHIVE_IA),
    });
  };

  /**
   * This function fetches data for all ineligible settings and updates state variables accordingly.
   *
   * @param selectedClient The selected client.
   * @param selectedCollateral The selected collateral.
   */
  const getAllValuesForIneligibleSettings = async (selectedClient: Required<IClient>, selectedCollateral: ICollateral) => {
    setIsIneligibleDetailsLoading(true);
    setIneligibleSettingDetails([]);
    setFetchedIneligibleSettingDetails([]);

    /* ineligible settings */
    const ineligibleSettings = selectedCollateral.collateralType === "AR"
      ? await getIneligibleSettings(selectedClient, selectedCollateral)
      : await getInvIneligibleSettings(selectedClient, selectedCollateral);

    const selectedIneligibleIndex = ineligibleSettings.findIndex(setting => !setting.disabled);

    /* rule, override related */
    const fetchedIneligibleSettingDetails = await Promise.all(getMapOfRequestsPerIneligibleSettingDetail(selectedCollateral, ineligibleSettings, selectedIneligibleIndex));

    return { ineligibleSettings, selectedIneligibleIndex, fetchedIneligibleSettingDetails };
  };

  const setAllValuesForIneligibleSettings = (ineligibleSettings: IIneligibleSettingAPI[], selectedIneligibleIndex: number, fetchedIneligibleSettingDetails: Array<IIneligibleSettingDetail>) => {
    /* set state values from api responses */
    ineligibleSettings.length > 0 && setSelectedIneligibleIndex(selectedIneligibleIndex);

    setIneligibleSettingDetails(fetchedIneligibleSettingDetails);
    setFetchedIneligibleSettingDetails(fetchedIneligibleSettingDetails);

    setFieldTables('UNLOADED');
    setFieldList('UNLOADED');
    setUserDefinedFieldList('UNLOADED');
    setFieldValues({});

    setAsOfDates('UNLOADED');
    setMostRecentlyUpdatedAsOfDate('UNLOADED');
    setSelectedAdjustmentsAsOfDate(null);
    setIsAdjustmentsDirty(false);
    setHasPaymentTerms('UNLOADED');
    setCurrency('UNLOADED');
    setParentCustomers('UNLOADED');
    setIsIneligibleDetailsLoading(false);
  };

  const getMapOfRequestsPerIneligibleSettingDetail = (selectedCollateral: ICollateral, ineligibleSettings: IIneligibleSettingAPI[], selectedIneligibleIndex: number) => ineligibleSettings.map(async (ineligibleSetting, index) => {
    const isIneligibleEnabledAndRequiringRule = !ineligibleSetting.disabled && ['DLQ', 'XA',  'AC', 'CN', 'CL', 'RFV'].includes(ineligibleSetting.code);
    if (selectedIneligibleIndex !== index && !isIneligibleEnabledAndRequiringRule) { return { ineligibleSetting }; }
    const isIneligibleDevoidOfOverrides = ['AC', 'CONTRA', 'RFV', 'ADJUSTMENTS'].includes(ineligibleSetting.code);
    const ineligibleRule = selectedCollateral.collateralType === "AR"
      ? await getIneligibleRule(selectedCollateral, ineligibleSetting)
      : await getInvIneligibleRule(selectedCollateral, ineligibleSetting);

    if (ineligibleRule === null) {
      const defaultIneligibleRule = getDefaultIneligibleRule(selectedCollateral, ineligibleSetting, hasPaymentTerms as boolean);
      return {
        ineligibleSetting,
        ineligibleRule: defaultIneligibleRule,
        ...(!isIneligibleDevoidOfOverrides && {ineligibleRuleOverrides: []})
      };
    }
    if (selectedIneligibleIndex !== index || isIneligibleDevoidOfOverrides) { return { ineligibleSetting, ineligibleRule }; }
    const ineligibleRuleOverrides = selectedCollateral.collateralType == "AR" 
      ? await getIneligibleRuleOverrides(ineligibleRule)
      : await getInvIneligibleRuleOverrides(ineligibleRule);

    const ruleOverrideDetails = await getIneligibleRuleOverrideDetails(selectedCollateral.collateralType, ineligibleRuleOverrides);
    return {
      ineligibleSetting,
      ineligibleRule: ineligibleRule,
      ineligibleRuleOverrides: ruleOverrideDetails
    };
  });

  /**
   * This function handles saving changes to ineligible settings, including creating, updating, and deleting rules and overrides.
   * @param values The form values representing the changes.
   * @param setSubmitting A function to set the submitting state.
   */
  const handleSave = async (values: IFormikValuesForRuleAndOverridesPerSetting, setSubmitting: (isSubmitting: boolean) => void) => {
    const nonNullSelectedCollateral = selectedCollateral as NonNullable<ICollateral>;
    try {
      setIsIneligibleDetailsLoading(true);
      setSubmitting(true);
      if (!hasCriteriaPermissions()) { return; }
      const savedChangesOnIneligibleSettingDetails = await Promise.all(values.ineligibleSettingDetails.map(async ineligibleSettingDetail => {
        const { ineligibleSetting, ineligibleRule } = ineligibleSettingDetail;
        const {
          ineligibleSetting: fetchedIneligibleSetting,
          ineligibleRule: fetchedIneligibleRule
        } = fetchedIneligibleSettingDetails.find(detail => detail.ineligibleSetting.code === ineligibleSetting.code) as IIneligibleSettingDetail;

        const savedSetting = !isObjectsEqual(ineligibleSetting, fetchedIneligibleSetting)
          ? ( selectedCollateral?.collateralType === 'AR'
              ? await putIneligibleSetting(ineligibleSetting)
              : await putInvIneligibleSetting(ineligibleSetting)
            )
          : ineligibleSetting;

        const isIneligibleRuleUnloaded = ineligibleRule === undefined;
        if (savedSetting.disabled || isIneligibleRuleUnloaded) { return ineligibleSettingDetail; }

        await handleDeleteForIneligibleRuleOverrides(ineligibleSettingDetail);

        const isIneligibleRuleNonExisting = ineligibleRule.ineligibleRuleId === NON_EXISTING;
        if (isIneligibleRuleNonExisting) {
          const {
            ineligibleRule: createdIneligibleRule,
            ineligibleRuleOverrides: createdIneligibleRuleOverrides
          } = await handleCreateNewIneligibleRule(nonNullSelectedCollateral, ineligibleSettingDetail);
          return {
            ineligibleSetting: savedSetting, ineligibleRule: createdIneligibleRule,
            ...(createdIneligibleRuleOverrides !== undefined && { ineligibleRuleOverrides: createdIneligibleRuleOverrides} )
          };
        }

        const [ savedRule, ...savedOverrides ] = await Promise.all([
          !isObjectsEqual(ineligibleRule, fetchedIneligibleRule) ? (
            selectedCollateral?.collateralType === 'INV'
              ? await putInvIneligibleRule(nonNullSelectedCollateral, ineligibleRule, ineligibleSetting)
              : await putIneligibleRule(nonNullSelectedCollateral, ineligibleRule, ineligibleSetting)
          ) : ineligibleRule,
          ...handleUpdatesForIneligibleRuleOverrides(ineligibleSettingDetail, fetchedIneligibleRule as IneligibleRuleAPI)
        ]);
        const isSettingDevoidOfOverrides = ['AC', 'RFV', 'CONTRA', 'ADJUSTMENTS'].includes(ineligibleSetting.code);
        return { ineligibleSetting: savedSetting, ineligibleRule: savedRule, ...(!isSettingDevoidOfOverrides && { ineligibleRuleOverrides: savedOverrides }) };
      }));
      setIneligibleSettingDetails(savedChangesOnIneligibleSettingDetails);
      setFetchedIneligibleSettingDetails(savedChangesOnIneligibleSettingDetails);
    }
    catch (error) { console.log('SAVING INELIGIBLES ERROR: ', error); }
    finally {
      setSubmitting(false);
      await timeout(1); /* to prevent loading state being immediately finished, even though other states are not yet rendered */
      setIsIneligibleDetailsLoading(false);
    }
  };

  /**
   * This function checks and retrieves criteria-related permissions, displaying a message if permissions are lacking.
   */
  const hasCriteriaPermissions = () => {
    const loadedPermissions = permissions as IIneligibleSettingsPermissions;
    let messages: string[] = [];
    if (!loadedPermissions.canAddCriteriaAndSettings) { messages.push('adding'); }
    if (!loadedPermissions.canUpdateCriteriaAndSettings) { messages.push('updating'); }
    if (!loadedPermissions.canDeleteCriteriaAndSettings) { messages.push('deleting'); }

    if (messages.length === 0) { return true; }
    const combinedMessages = messages.reduce((combinedMessage, currentMessage) => `${combinedMessage}/${currentMessage}`);
    setToaster({
      isOpen: true, severity: 'error',
      message: `You do not have the necessary permissions for ${combinedMessages} criteria.`
    });
    return false;
  };

  /**
   * This function handles creating a new ineligible rule when it is new (all values inside are also new).
   * @param selectedCollateral The selected collateral.
   * @param ineligibleSettingDetail Details of the ineligible setting.
   * @param ineligibleSetting The ineligible setting API data.
   */
  const handleCreateNewIneligibleRule = async (selectedCollateral: ICollateral, ineligibleSettingDetail: IIneligibleSettingDetail) => { // api requests when ineligible rule is new (means all values inside are also new)
    const { ineligibleSetting, ineligibleRule, ineligibleRuleOverrides } = ineligibleSettingDetail;
    const rule = (selectedCollateral?.collateralType === 'AR'
      ? await postIneligibleRule(selectedCollateral, ineligibleRule as IneligibleRuleAPI, ineligibleSetting)
      : await postInvIneligibleRule(selectedCollateral, ineligibleRule as IneligibleRuleAPI, ineligibleSetting)
    );
    if (ineligibleRuleOverrides === undefined || ineligibleRuleOverrides.length === 0) { return { ineligibleRule: rule }; }

    const savedOverrides = await Promise.all(ineligibleRuleOverrides.map(override => handleCreateOverrideAndConnections(rule, override)));
    return { ineligibleRule: rule, ineligibleRuleOverrides: savedOverrides };
  };

  /**
   * This function handles updates for ineligible rule overrides.
   * @param ineligibleSettingDetail Details of the ineligible setting.
   * @returns An array of promises for updating rule overrides.
   */
  const handleUpdatesForIneligibleRuleOverrides = (ineligibleSettingDetail: IIneligibleSettingDetail, fetchedRule: IneligibleRuleAPI) => {
    if (ineligibleSettingDetail?.ineligibleRuleOverrides === undefined) { return []; }
    const collateralType = ineligibleSettingDetail.ineligibleSetting.invCollateralId == null ? 'AR' : 'INV'
    const { ineligibleRuleOverrides: fetchedIneligibleRuleOverrides } = fetchedIneligibleSettingDetails.find(detail => detail.ineligibleSetting.code === ineligibleSettingDetail.ineligibleSetting.code) as IIneligibleSettingDetail;
    const loadedFetchedIneligibleRuleOverrides = fetchedIneligibleRuleOverrides as IIneligibleRuleOverride[] ;

    return ineligibleSettingDetail?.ineligibleRuleOverrides.map(async overrideFormData => { // api requests for updating (post and put), put requests does not need an await after as response id will not change
      const IS_OVERRIDE_NON_EXISTING = overrideFormData.ineligibleRuleOverrideId === NON_EXISTING;
      if (IS_OVERRIDE_NON_EXISTING) { return handleCreateOverrideAndConnections(fetchedRule, overrideFormData); }

      const fetchedOverride = loadedFetchedIneligibleRuleOverrides.find(override => override.ineligibleRuleOverrideId === overrideFormData.ineligibleRuleOverrideId) as IIneligibleRuleOverride;
      const [ savedOverride, ...savedConditionGroups ] = await Promise.all([
        !isObjectsEqual(overrideFormData, fetchedOverride) ? (
          collateralType === 'AR'
            ? await putIneligibleOverrides(overrideFormData, fetchedRule) 
            : await putInvIneligibleOverrides(overrideFormData, fetchedRule)
        ) : overrideFormData,
        ...handleUpdatesForIneligibleRuleConditionGroups(collateralType, overrideFormData, fetchedOverride)
      ]);
      return { ...savedOverride, ineligibleRuleConditionGroup: savedConditionGroups };
    });
  };

  const handleDeleteForIneligibleRuleOverrides = async (formikSetting: IIneligibleSettingDetail) => {
    const ineligibleSettingDetail = fetchedIneligibleSettingDetails.find(
        detail => detail.ineligibleSetting.ineligibleSettingId === formikSetting.ineligibleSetting.ineligibleSettingId
    ) as IIneligibleSettingDetail;

    if (ineligibleSettingDetail?.ineligibleRuleOverrides === undefined) { return []; }

    const handleDeleteOverrideAndConnections = async (override: IIneligibleRuleOverride) => {
      await Promise.all(override.ineligibleRuleConditionGroup.map(conditionGroup => handleDeleteConditionGroupAndConnections(override, conditionGroup)));
      return handleDeleteOverride(override, formikSetting);
    };

    const handleDeleteConditionGroupAndConnections = async (override: IIneligibleRuleOverride, conditionGroup: IIneligibleRuleConditionGroup) => {
      await Promise.all(conditionGroup.ineligibleRuleCondition.map(condition => handleDeleteCondition(override, conditionGroup, condition, formikSetting)));
      return handleDeleteConditionGroup(override, conditionGroup, formikSetting);
    };

    return Promise.all(ineligibleSettingDetail?.ineligibleRuleOverrides.map(override => handleDeleteOverrideAndConnections(override)));
  };

  /**
   * This function checks if the ineligible settings are empty.
   * @param formik The Formik props.
   * @returns True if ineligible settings are empty, otherwise false.
   */
  const isIneligibleSettingsEmpty = (formik: FormikProps<IFormikValuesForRuleAndOverridesPerSetting>) => {
    if (isIneligibleDetailsLoading) { return false; } /* do not show empty state while loading */
    return selectedCollateral === null || formik.values.ineligibleSettingDetails.length === 0;
  };

  const hasUnsavedChanges = (formikDetails: IIneligibleSettingDetail[], fetchedDetails: IIneligibleSettingDetail[]) => {
    return formikDetails.some((formikDetail) => {
      const fetchedDetail = fetchedDetails.find(fetchedDetail => fetchedDetail.ineligibleSetting.code=== formikDetail.ineligibleSetting.code);
      return !isObjectsEqual(formikDetail, fetchedDetail ?? {});
    });
  };

  const isIneligibleRuleAndOverrideLoading = (formik: FormikProps<IFormikValuesForRuleAndOverridesPerSetting>) => {
    if (isIneligibleDetailsLoading) { return true; }
    if (selectedIneligibleIndex === NON_EXISTING) { return false; }
    const overrides = formik.values.ineligibleSettingDetails[selectedIneligibleIndex]?.ineligibleRuleOverrides;
    const isRuleLoading = formik.values.ineligibleSettingDetails[selectedIneligibleIndex]?.ineligibleRule === undefined;
    const isOverrideLoading = overrides === undefined;
    const isIneligibleDevoidOfOverrides = ['AC', 'CONTRA', 'RFV', 'ADJUSTMENTS'].includes(formik.values.ineligibleSettingDetails[selectedIneligibleIndex]?.ineligibleSetting.code);
    if (isIneligibleDevoidOfOverrides) { return isRuleLoading; }
    if (isRuleLoading || isOverrideLoading) { return true; }
    const isDelinquentLoading = formik.values.ineligibleSettingDetails[selectedIneligibleIndex]?.ineligibleSetting.code === 'DLQ' && hasPaymentTerms === 'UNLOADED';
    if (isDelinquentLoading) { return true; }
    const hasOverrides = overrides.length > 0;
    const isFieldRelatedValuesLoading = hasOverrides && [fieldTables, fieldList, userDefinedFieldList, parentCustomers].includes('UNLOADED');
    return isFieldRelatedValuesLoading;
  };

  /**
   * This function handles changes to the Formik reference.
   * @param node The Formik node.
   */
  const formikRef = (node: FormikProps<IFormikValuesForRuleAndOverridesPerSetting>) => {
    if (node === null || isIneligibleDetailsLoading) { return; }
    const hasUnsavedChangesOnFormik = hasUnsavedChanges(node.values.ineligibleSettingDetails, fetchedIneligibleSettingDetails as IIneligibleSettingDetail[]);
    const combinedDirty = hasUnsavedChangesOnFormik || isAdjustmentsDirty;
    if (combinedDirty === isFormikDirty) { return; }
    setIsFormikDirty(combinedDirty);
  };

  const shouldValidate = useRef(false);

  return (
    <Box key={`formContainerForCollateral${selectedCollateral?.recordId}`} sx={{...(!(permissions as IIneligibleSettingsPermissions).canViewIneligibleSettings && styles.hidden)}}>
      <Formik
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
        innerRef={formikRef}
        validate={({ineligibleSettingDetails}) => {
            if (!shouldValidate.current) return {};
            return validateIneligibleSettings({ ineligibleSettingDetails })
          }
        }
        initialValues={{ ineligibleSettingDetails } as IFormikValuesForRuleAndOverridesPerSetting}
        onReset={(_values, formikHelpers) => {
          formikHelpers.setErrors({});
          !isIneligibleDetailsLoading && setIneligibleSettingDetails(fetchedIneligibleSettingDetails);
        }}
        onSubmit={(values, { setSubmitting }) => handleSave(values, setSubmitting)}
      >
        {(formik) => (
          <>
            <Box sx={styles.titleContainer}>
              <Typography tabIndex={0} variant='h6' component='h3' sx={styles.title}>
                Ineligible Settings
              </Typography>
              <Box sx={styles.rowBox}>
                { isFormikDirty ? (
                  <Button
                    disabled={isIneligibleDetailsLoading}
                    variant='outlined'
                    sx={styles.cancelButton}
                    onClick={async () => {
                      setIsIneligibleDetailsLoading(true);
                      await timeout(1); /* to render loading state, causing a rerender (solution for cases not rendering immediately) */
                      formik.resetForm();
                      adjustmentsFormikRef.current?.resetForm();
                      ineligibleSettingDetails.length > 0 && setSelectedIneligibleIndex(ineligibleSettingDetails.findIndex(detail => !detail.ineligibleSetting.disabled));
                      setIsIneligibleDetailsLoading(false);
                    }}
                  >
                    Cancel
                  </Button>
                ) : null }
                <DisabledComponentsContainer isDisabled={(!isFormikDirty || formik.isSubmitting || formik.isValidating || isIneligibleRuleAndOverrideLoading(formik))}>
                  <Button
                    disabled={(!isFormikDirty || formik.isSubmitting || formik.isValidating || isIneligibleRuleAndOverrideLoading(formik))}
                    aria-label={(!isFormikDirty || formik.isSubmitting || formik.isValidating || isIneligibleRuleAndOverrideLoading(formik)) ? 'Save button disabled' : 'Save'}
                    color='primary'
                    variant='contained'
                    sx={styles.saveButton}
                    onClick={async () => {
                      shouldValidate.current = true;
                      const hasError = await getErrors(formik);
                      const adjustmentsErrors = adjustmentsFormikRef.current ? await adjustmentsFormikRef.current.validateForm() : {};
                      if (hasError || Object.keys(adjustmentsErrors).length > 0) { setIsInvalidIneligibleSettingsMessageModalOpen(true); return; }
                      shouldValidate.current = false;
                      await Promise.all([
                        formik.submitForm(),
                        adjustmentsFormikRef.current?.dirty ? adjustmentsFormikRef.current.submitForm() : Promise.resolve(),
                      ]);
                      setToaster({ isOpen: true, severity: 'success', message: 'Ineligible Settings have been successfully saved.' });
                    }}
                  >
                    Save
                  </Button>
                </DisabledComponentsContainer>
              </Box>
            </Box>
            <ARCollateralHeader
              formik={formik}
              shouldValidate={shouldValidate}
              adjustmentsFormikRef={adjustmentsFormikRef}
              onReload={reloadIneligibleSettings}
            />
            <Box sx={styles.contentStyle}>
              {
                (isIneligibleSettingsEmpty(formik))
                  ? <NoDataPlaceholder messageContainerStyle={styles.noDataPlaceHolderContainer} messageTextStyle={styles.noDataPlaceHolderText} />
                  : (
                    <>
                      <IneligibleList formik={formik} />
                      <IneligibleDetails
                        formik={formik}
                        adjustmentsFormikRef={adjustmentsFormikRef}
                      />
                      <InvalidIneligibleSettingsMessageModal
                        isOpen={isInvalidIneligibleSettingsMessageModalOpen}
                        handleClose={() => setIsInvalidIneligibleSettingsMessageModalOpen(false)}
                      />
                    </>
                  )
              }
            </Box>
            <Toaster
              open={toaster.isOpen}
              message={toaster.message}
              severity={toaster.severity}
              onCloseChange={() => setToaster({ isOpen: false, message: '', severity: 'success' })}
            />
          </>
        )}
      </Formik>
    </Box>
  );
};

export default IneligibleSettingsARFormContainer;
